/**
 * Third-party risk — vendor questionnaire posture bands and the critical-cap.
 *
 * WHY THIS LIVES IN SHARED, NOT IN THE API
 * Three readers need the same definitions and must never drift apart:
 *   * the seed, which decides WHICH questions are critical when it authors the
 *     content;
 *   * the API scoring path, which applies the cap when a supplier submits;
 *   * the web posture dashboard, which buckets every vendor into one of the
 *     three bands for the stat tiles.
 * If "critical" or "Aligned" meant one thing on the server and another on the
 * screen, the dashboard would contradict the score it is drawn from. One
 * definition, imported everywhere, is the whole point.
 *
 * THE BANDS
 * A vendor's completed questionnaire lands in exactly one of three buckets, and
 * these are the buckets the dashboard tiles count:
 *   * ALIGNED               — no meaningful gaps (score at or above the aligned
 *                             threshold, and no critical control failed).
 *   * WITH_GAPS             — minor gaps (score in the middle band).
 *   * WITH_SIGNIFICANT_GAPS — a critical control failed, OR the score is below
 *                             the gaps threshold.
 *
 * THE CRITICAL-CAP (the defensible part)
 * A handful of controls are load-bearing: no MFA on admin/remote access, no
 * incident response, no backups. A supplier can answer everything else
 * perfectly and still be a serious supply-chain risk if one of these is absent.
 * So a critical failure CAPS the result below the gaps threshold regardless of
 * the raw total — a vendor failing a critical control cannot be shown as
 * "Aligned" or even "With Gaps". The cap is applied to the stored 0–100
 * `complianceScore` itself (see api/modules/tprm scoring), so it flows
 * unchanged into `complianceRateToRisk`, the risk chart and these tiles with no
 * second source of truth to keep in step.
 */
/** The three posture buckets, in order of increasing concern. */
export const VENDOR_COMPLIANCE_BANDS = [
    'ALIGNED',
    'WITH_GAPS',
    'WITH_SIGNIFICANT_GAPS',
];
/** At or above this 0–100 score (and no critical failure): Aligned. */
export const VENDOR_ALIGNED_MIN = 85;
/** At or above this score (and below Aligned): With Gaps. Below it: Significant. */
export const VENDOR_GAPS_MIN = 60;
/**
 * Band a stored (already capped) 0–100 compliance score.
 *
 * Takes the score ALONE on purpose: a critical failure is folded into the score
 * by `cappedComplianceScore` at submission time, so by the time the dashboard
 * reads a row there is nothing left to special-case. One number in, one band
 * out, the same on the server and in the browser.
 */
export function vendorComplianceBand(score) {
    if (score >= VENDOR_ALIGNED_MIN)
        return 'ALIGNED';
    if (score >= VENDOR_GAPS_MIN)
        return 'WITH_GAPS';
    return 'WITH_SIGNIFICANT_GAPS';
}
/**
 * Apply the critical-cap to a raw 0–100 compliance rate.
 *
 * A critical failure forces the score just below the gaps threshold, which puts
 * the band at WITH_SIGNIFICANT_GAPS no matter how high the raw total was. We cap
 * to exactly `VENDOR_GAPS_MIN - 1` rather than to zero deliberately: the point
 * is to state the BAND ("this supplier has significant gaps"), not to pretend a
 * supplier who scored 92% but has no backups actually scored nothing. The full
 * per-answer detail is still on the assessment for whoever follows up.
 */
export function cappedComplianceScore(rawScore, criticalFailed) {
    const clamped = Math.min(100, Math.max(0, Math.round(rawScore)));
    if (!criticalFailed)
        return clamped;
    return Math.min(clamped, VENDOR_GAPS_MIN - 1);
}
/**
 * Question codes that CAP the result when they fail.
 *
 * Stable, semantic codes — not positional — so re-ordering or inserting a
 * question never silently changes which controls are load-bearing. The seed
 * content assigns exactly these codes to exactly these questions on the two
 * supplier questionnaires; the scorer checks membership by code.
 *
 *   SSA-* — the long "Supplier Security Assessment (NIS2/DORA)"
 *   SSS-* — the short "Supplier Security Snapshot"
 *
 * The three controls, on both forms:
 *   MFA on admin/remote access (NIS2 Art. 21(2)(j)),
 *   a documented incident-response capability (NIS2 Art. 21(2)(b) / Art. 23),
 *   backups with a tested restore (NIS2 Art. 21(2)(c)).
 */
export const CRITICAL_SUPPLIER_QUESTION_CODES = [
    'SSA-IAM-MFA',
    'SSA-IR-PLAN',
    'SSA-BCP-BACKUP',
    'SSS-MFA',
    'SSS-IR',
    'SSS-BACKUP',
];
//# sourceMappingURL=tprm.js.map